import { useEffect, useState, memo } from 'react';
import {
    Box, Button, Flex, Heading, Text, Image, Divider, HStack, VStack, Badge,
    Modal, ModalOverlay, ModalContent, ModalBody, ModalFooter, ModalCloseButton,
    Spinner, Alert, AlertIcon, UnorderedList, ListItem, CloseButton,
    useToast, useDisclosure,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isNil, omitBy } from 'lodash';
import { CheckCircleIcon, LockIcon, TimeIcon } from '@chakra-ui/icons';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

import Paystack from '@paystack/inline-js';
import { useStartButton } from '../../../hooks/useStartButton';
import { formatPhoneNumberInternational } from 'utils/phoneformatter';
import { apiService } from 'services/service';
import { view_cart } from 'redux/actions/productActions';
import { useCurrency, useNairaToUSDToCurrency } from 'hooks/useCurrency';
import { setCurrencyValue } from 'redux/actions/currencyActions';
import useInventoryFinancingCheck from 'hooks/useInventoryFinancingCheck';
import useGetUserDetails from 'hooks/useGetUserDetail';
import WalletHistoryPrice from 'components/Wallet/WalletHistoryPrice';
import paystack from '../../../assets/paystack-logo.png';
import startButton from '../../../assets/startbutton-logo.jpg';

countries.registerLocale(en);

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const DANGER      = '#E53E3E';

const FONT      = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const CheckIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.6"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const SecureLockIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <rect x="3" y="11" width="18" height="11" rx="2"
            stroke="currentColor" strokeWidth="1.8" />
        <path d="M7 11V7a5 5 0 0110 0v4"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   STORES — preserved
   ───────────────────────────────────────────────────────────── */
const STORES = [
    {
        id: 'opebi',
        carrier_id: 'opebi',
        name: 'Warehouse Pickup Opebi obana',
        address: {
            line1: '77 Opebi road',
            city: 'Ikeja',
            state: { label: 'Lagos', value: 'Lagos' },
            country: { label: 'NG', value: 'Nigeria' },
            phone: '+2348069331070',
        },
        currency: 'NGN',
        amount: 0,
        delivery_time: 'Same-day Pickup',
        carrier_rate_description: 'Fulfilment Centre Pickup',
    },
];

const formatStoreAddressForPayload = (store) => ({
    first_name: store.contact_first_name || 'Fulfilment',
    last_name: store.contact_last_name || 'Centre',
    email: store.email || 'obana.africa@gmail.com',
    phone: store.address.phone || '',
    is_residential: false,
    line1: store.address.line1,
    line2: store.address.line2 || '',
    city: store.address.city,
    state: store.address.state?.value || store.address.state,
    country: store.address.country.label,
    zip: store.address.zip || '',
    metadata: { customerId: store.id },
    useExistingAddress: false,
});

/* ─────────────────────────────────────────────────────────────
   PAYMENT METHOD CARD
   ───────────────────────────────────────────────────────────── */
const PaymentMethodCard = memo(function PaymentMethodCard({
    id, name, description, logo, logoHeight,
    isSelected, isDisabled, disabledReason, onSelect,
    rightBadge, helperText, isDashed,
}) {
    return (
        <Box
            as="button"
            type="button"
            onClick={() => !isDisabled && onSelect(id)}
            disabled={isDisabled}
            bg={SURFACE}
            border="2px"
            borderStyle={isDashed ? 'dashed' : 'solid'}
            borderColor={isSelected ? NAVY : BORDER}
            borderRadius="12px"
            p="14px 16px"
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            opacity={isDisabled ? 0.55 : 1}
            textAlign="left"
            w="full"
            transition="all 0.15s ease"
            boxShadow={isSelected ? `0 0 0 3px ${CYAN}` : 'none'}
            _hover={
                isDisabled
                    ? undefined
                    : {
                          borderColor: isSelected ? NAVY : '#CBD5E0',
                          transform: 'translateY(-1px)',
                      }
            }
            _focusVisible={{ outline: `2px solid ${NAVY}`, outlineOffset: '2px' }}
            aria-label={`Pay with ${name}${isDisabled ? ` (${disabledReason})` : ''}`}
            aria-pressed={isSelected}
        >
            <Flex justify="space-between" align="flex-start" gap="12px">
                <Flex align="flex-start" gap="12px" minW="0" flex="1">
                    <Flex
                        w="22px"
                        h="22px"
                        borderRadius="full"
                        border="2px solid"
                        borderColor={isSelected ? NAVY : BORDER}
                        bg={isSelected ? NAVY : 'transparent'}
                        color="white"
                        align="center"
                        justify="center"
                        flexShrink={0}
                        mt="2px"
                        transition="all 0.15s ease"
                    >
                        {isSelected && <CheckIcon />}
                    </Flex>
                    <Box minW="0" flex="1">
                        <Flex align="center" gap="8px" flexWrap="wrap" mb="2px">
                            <Text fontSize="13px" fontWeight="700" color={INK} fontFamily={FONT} lineHeight="1.3">
                                {name}
                            </Text>
                            {rightBadge}
                        </Flex>
                        {description && <Text fontSize="11px" color={MUTED} lineHeight="1.4">{description}</Text>}
                        {isDisabled && disabledReason && <Text fontSize="10.5px" color={DANGER} fontWeight="600" mt="4px">{disabledReason}</Text>}
                        {helperText && !isDisabled && <Text fontSize="10.5px" color={NAVY} fontWeight="700" mt="4px">{helperText}</Text>}
                    </Box>
                </Flex>
                {logo && (
                    <Box flexShrink={0}>
                        <Image src={logo} alt={`${name} logo`} height={logoHeight || '24px'} width="auto" objectFit="contain" opacity={isDisabled ? 0.4 : 1} />
                    </Box>
                )}
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   SUMMARY ROW
   ───────────────────────────────────────────────────────────── */
const SummaryCard = memo(function SummaryCard({ label, title, lines, onChange }) {
    return (
        <Box bg={SURFACE} border="1px solid" borderColor={BORDER} borderRadius="10px" p="14px">
            <Flex justify="space-between" align="flex-start" gap="12px">
                <Box minW="0" flex="1">
                    <Text fontSize="10px" color={FAINT} fontWeight="700" letterSpacing="0.08em" mb="6px">{label}</Text>
                    {title && <Text fontSize="13px" fontWeight="700" color={INK} fontFamily={FONT} mb="2px" noOfLines={1}>{title}</Text>}
                    {lines.filter(Boolean).map((line, i) => (
                        <Text key={i} fontSize="11.5px" color={MUTED} lineHeight="1.4" noOfLines={2}>{line}</Text>
                    ))}
                </Box>
                <Button size="xs" variant="outline" borderColor={BORDER} color={NAVY} fontWeight="700" fontSize="11px" h="28px" borderRadius="7px" flexShrink={0} _hover={{ borderColor: NAVY, bg: SURFACE_ALT }} onClick={onChange}>Change</Button>
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   ONPSS GATE MODAL
   ───────────────────────────────────────────────────────────── */
const ONPSSGateModal = ({ isOpen, onClose, hasApplication }) => (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
        <ModalContent borderRadius="16px" overflow="hidden" fontFamily={FONT}>
            <Box bg={hasApplication ? 'orange.50' : NAVY} color={hasApplication ? 'orange.800' : 'white'} borderBottomWidth="1px" borderColor={hasApplication ? 'orange.200' : 'transparent'} p="20px" position="relative">
                <HStack spacing={3}>
                    <Box bg={hasApplication ? 'orange.100' : 'whiteAlpha.200'} borderRadius="full" p={2}>
                        {hasApplication ? <TimeIcon color="orange.500" boxSize={5} /> : <LockIcon color="white" boxSize={5} />}
                    </Box>
                    <Box>
                        <Text fontWeight="800" fontSize="16px" lineHeight="1.2" fontFamily={FONT}>
                            {hasApplication ? 'Application under review' : 'Customer not registered'}
                        </Text>
                        <Text fontSize="11.5px" opacity={0.85} mt="2px">ONPSS · Order Now, Pay Small Small</Text>
                    </Box>
                </HStack>
                <ModalCloseButton color={hasApplication ? 'orange.700' : 'white'} _hover={{ bg: hasApplication ? 'orange.100' : 'whiteAlpha.300' }} />
            </Box>
            <ModalBody p="20px">
                {hasApplication ? (
                    <VStack align="stretch" spacing="14px">
                        <Alert status="warning" borderRadius="10px" fontSize="12.5px">
                            <AlertIcon />
                            This customer's inventory financing application has been received and is currently under review. ONPSS will be available once approved.
                        </Alert>
                        <Box bg={SURFACE_ALT} borderRadius="10px" p="14px" borderWidth="1px" borderColor={BORDER}>
                            <Text fontSize="12px" fontWeight="800" color={INK} mb="8px" fontFamily={FONT}>What happens next?</Text>
                            <UnorderedList spacing="6px" fontSize="12px" color={MUTED} pl="14px">
                                <ListItem>Our team verifies the customer's documents &amp; business</ListItem>
                                <ListItem>Customer receives an email with the decision</ListItem>
                                <ListItem>Once approved, ONPSS is available at checkout</ListItem>
                            </UnorderedList>
                        </Box>
                    </VStack>
                ) : (
                    <VStack align="stretch" spacing="14px">
                        <Alert status="info" borderRadius="10px" fontSize="12.5px">
                            <AlertIcon />
                            <Box>This customer hasn't registered for <strong>Order Now, Pay Small Small</strong>. They need to log in to the platform and complete the inventory financing registration before this payment option can be used.</Box>
                        </Alert>
                        <Box bg="blue.50" borderRadius="10px" p="14px" borderWidth="1px" borderColor="blue.200">
                            <Text fontSize="12px" fontWeight="800" color="blue.700" mb="8px" fontFamily={FONT}>How to get the customer registered:</Text>
                            <UnorderedList spacing="6px" fontSize="12px" color="blue.700" pl="14px">
                                <ListItem>Ask the customer to log in to <strong>obana.africa</strong></ListItem>
                                <ListItem>Direct them to the <strong>ONPSS / Inventory Financing</strong> section</ListItem>
                                <ListItem>They complete the form and upload required documents</ListItem>
                                <ListItem>Once approved, return here to checkout with ONPSS</ListItem>
                            </UnorderedList>
                        </Box>
                    </VStack>
                )}
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor={BORDER} p="14px 20px">
                <Button variant="outline" onClick={onClose} borderColor={BORDER} color={INK} fontWeight="700" fontSize="12.5px" fontFamily={FONT} borderRadius="9px" h="40px" flex={1} _hover={{ borderColor: NAVY, bg: SURFACE_ALT }}>Choose another method</Button>
            </ModalFooter>
        </ModalContent>
    </Modal>
);

/* ─────────────────────────────────────────────────────────────
   MAIN — PAYMENT OPTIONS
   ───────────────────────────────────────────────────────────── */
const PaymentOptions = ({ setTabIndex, quote, checkoutPayload, onPaymentSuccess }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const toast = useToast();
    const { flattenedUser: userDetails } = useGetUserDetails();

    const selectedCurrency = useSelector((state) => state.currencyReducer.currenciesRates);
    const convertedAmount = useCurrency(checkoutPayload?.cartItems?.cart_total_price);
    const newQuote = useNairaToUSDToCurrency(quote);
    const { initializePayment } = useStartButton();
    const [showONPSSGate, setShowONPSSGate] = useState(false);
    const zohoContactId = checkoutPayload?.customer_id;
    const { hasExistingApplication, isLoading: isCheckingONPSS } = useInventoryFinancingCheck(zohoContactId);
    const onpssUnlocked = hasExistingApplication;
    const isNGN = convertedAmount?.code === 'NGN';
    const sellRate = Number(selectedCurrency?.sell);
    const exchangeRate = sellRate && sellRate !== 0 ? 1 / sellRate : 1;
    const { isOpen: isSaladSuccessOpen, onOpen: onSaladSuccessOpen, onClose: onSaladSuccessClose } = useDisclosure();

    useEffect(() => {
        if (checkoutPayload?.address?.country) {
            dispatch(setCurrencyValue(checkoutPayload.address.country));
        }
    }, [dispatch, checkoutPayload?.address?.country]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const notifyQuoteSubmitted = () => {
        toast({
            duration: null,
            isClosable: false,
            position: 'top-center',
            render: ({ onClose }) => (
                <Box color="white" p={4} bg="#1b3b5f" borderRadius="md" boxShadow="lg" maxW="400px">
                    <Flex justify="space-between" align="center" mb={2}>
                        <Text fontWeight="bold" fontSize="lg">Quote Submitted!</Text>
                        <CloseButton size="sm" onClick={onClose} />
                    </Flex>
                    <Text fontSize="sm" mb={4}>
                       Thank you for your order. Your order has been successfully created, and we will reach out to you as soon as it is confirmed.
                    </Text>
                    <Flex gap={3} justify="flex-end">
                        <Button size="sm" variant="ghost" color="white" onClick={onClose}>Dismiss</Button>
                        <Button size="sm" colorScheme="teal" onClick={() => { navigate('/dashboard'); onClose(); }}>Create Another Order</Button>
                    </Flex>
                </Box>
            ),
        });
    };

    const buildOrderPayload = () => {
        const isPickup = checkoutPayload?.dispatchType === 'pickup';
        const deliveryAddress = isPickup
            ? formatStoreAddressForPayload(STORES[0])
            : {
                  ...checkoutPayload?.address,
                  phone: formatPhoneNumberInternational(checkoutPayload?.address?.phone, checkoutPayload?.address?.country)?.split(' ').join(''),
              };
        let deliveryType = checkoutPayload?.delivery_type;
        if (!deliveryType) {
            if (checkoutPayload?.aggregated_carrier_mapping) deliveryType = 'aggregated';
            else if (checkoutPayload?.isMultiVendor) deliveryType = 'per-vendor';
            else deliveryType = 'single';
        }
        const isPerVendor = deliveryType === 'per-vendor';
        const isAggregated = deliveryType === 'aggregated';
        const isSingleVendor = deliveryType === 'single';
        const isMultiVendor = checkoutPayload?.isMultiVendor;

        let payload = {
            customer_id: checkoutPayload?.customer_id,
            delivery_address: deliveryAddress,
            amount: convertedAmount?.price,
            shipping_fee: newQuote?.price,
            currency: { symbol: selectedCurrency?.symbol === 'NGN' ? '₦' : selectedCurrency?.symbol, rate: selectedCurrency?.sell, code: selectedCurrency?.code },
            cf_exchange_rate: exchangeRate,
            delivery_type: deliveryType,
            isMultiVendor,
            dispatchType: checkoutPayload?.dispatchType,
            pickUpMethod: checkoutPayload?.pickUpMethod,
        };
        if (isPerVendor && checkoutPayload?.vendor_selections) {
            payload.vendor_selections = checkoutPayload.vendor_selections.map((selection) => ({
                vendor_id: selection.vendor_id,
                pickup_address: selection.pickup_address,
                rate_id: selection.selected_rate_id,
                actual_rate_id: selection.selected_rate_id,
                carrier_reference: selection.selected_carrier_reference,
                items: selection.items.map((item) => ({
                    item_id: item.item_id,
                    name: item.name,
                    description: item.description || item.name,
                    quantity: item.quantity || 1,
                    value: item.value || item.total_price || 0,
                    weight: item.cf_weight || 1,
                    currency: 'NGN',
                    pickup_address: item.pickup_address,
                })),
                cost: selection.cost,
            }));
        } else if (isAggregated && isMultiVendor && checkoutPayload?.vendorGroups) {
            payload.rate_id = checkoutPayload?.rate_id;
            payload.carrier_reference = checkoutPayload?.selectedCarrierReference;
            payload.vendor_groups = checkoutPayload.vendorGroups.map((group) => ({
                vendor_id: group.vendor_id,
                pickup_address: group.pickup_address,
                vendor_rate_id: group.vendor_rate_id,
                actual_rate_id: group.vendor_rate_id || group.actual_rate_id || null,
                carrier_reference: group.carrier_reference,
                items: group.items.map((item) => ({
                    item_id: item.item_id,
                    name: item.name,
                    description: item.description || item.name,
                    quantity: item.quantity || 1,
                    value: item.value || item.total_price || 0,
                    weight: item.cf_weight || 1,
                    currency: 'NGN',
                    pickup_address: item.pickup_address,
                })),
            }));
        } else if (isSingleVendor || !isMultiVendor) {
            payload.items = checkoutPayload?.cartItems?.items?.map((item) => ({
                item_id: item?.item_id,
                name: item?.name,
                description: item?.description ?? item?.name,
                quantity: item?.quantity ?? 1,
                price: item?.price ?? 0,
                total_price: item?.total_price ?? 0,
                weight: item?.cf_weight || 1,
                currency: 'NGN',
                pickup_address: item?.pickup_address,
            }));
            if (!isPickup) {
                payload.rate_id = checkoutPayload?.rate_id;
                payload.carrier_reference = checkoutPayload?.dispatcher?.carrier_reference;
            }
        }
        if (checkoutPayload?.dispatcher) payload.dispatcher = checkoutPayload.dispatcher;
        return payload;
    };

    const makePayment = async (orderId, selectedOption) => {
        if (selectedOption === 'COD') {
            onSaladSuccessOpen();
        } else if (selectedOption === 'ONPSS') {
            toast({ title: 'Order placed — ONPSS activated', description: "The customer's instalment plan has been activated.", status: 'success', duration: 5000, isClosable: true });
            onPaymentSuccess();
            navigate('/dashboard');
            dispatch(view_cart());
        } else if (selectedOption === 'PAYSTACK') {
            const popup = new Paystack();
            popup.checkout({
                key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_df60dcfa8294d512014a61bbf99365693b238608',
                email: checkoutPayload?.address?.email,
                amount: (convertedAmount?.price + quote) * 100,
                currency: convertedAmount?.code,
                metaData: checkoutPayload?.address,
                reference: orderId,
                onSuccess: () => { toast({ title: 'Payment successful', status: 'success', duration: 3000, isClosable: true }); navigate('/dashboard'); dispatch(view_cart()); },
                onCancel: () => { toast({ title: 'Payment cancelled', status: 'warning', duration: 3000, isClosable: true }); dispatch(view_cart()); },
                onError: (error) => { dispatch(view_cart()); toast({ title: 'Payment error', description: error.message || 'Payment failed', status: 'error', duration: 3000, isClosable: true }); },
            });
        } else if (selectedOption === 'STARTBUTTON') {
            const paymentMethodsMap = {
                NGN: ['bank', 'card', 'bank_transfer', 'ussd', 'payattitude'],
                GHS: ['card', 'mobile_money'],
                ZAR: ['eft', 'qr', 'card'],
                KES: ['card', 'mobile_money'],
                UGX: ['card', 'mobile_money'],
                RWF: ['mobile_money'],
                XOF: ['mobile_money'],
                XAF: ['mobile_money'],
            };
            await initializePayment({
                phone: checkoutPayload?.address?.phone?.replace(/\D/g, ''),
                key: process.env.REACT_APP_STARTBUTTON_PUBLIC_KEY,
                amount: convertedAmount?.price * 100,
                paymentMethods: paymentMethodsMap[convertedAmount?.code] || ['card'],
                env: 'test',
                currency: convertedAmount?.code,
                email: checkoutPayload?.address?.email,
                reference: orderId,
                metadata: checkoutPayload?.address,
                success: () => { navigate('/dashboard/orders'); dispatch(view_cart()); },
                close: () => dispatch(view_cart()),
                error: (error) => { dispatch(view_cart()); toast({ title: 'Payment error', description: error?.statusText || 'Payment failed', status: 'error', duration: 3000, isClosable: true }); navigate('/dashboard/orders'); },
            });
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        if (values.selectedOption === 'ONPSS' && !onpssUnlocked) {
            setShowONPSSGate(true);
            setSubmitting(false);
            return;
        }
        setSubmitting(true);
        try {
            const isPickup = checkoutPayload?.dispatchType === 'pickup';
            const payload = buildOrderPayload();
            const params = omitBy({ deliveryMethod: isPickup ? 'pickup' : 'shipment', paymentMethod: values?.selectedOption, pickUpMethod: checkoutPayload?.pickUpMethod || 'delivery' }, isNil);
            const payload1 = {
                customer_id: payload.customer_id,
                shipping_charge: payload.shipping_fee / (payload.cf_exchange_rate === 1 ? 1470 : payload.cf_exchange_rate),
                custom_fields: [
                    { value: `${userDetails?.attributes?.first_name || ''} ${userDetails?.attributes?.last_name || ''}`, label: 'SALESPERSON NAME' },
                    { label: 'META_DATA', value: { sales_person_name: `${userDetails?.attributes?.first_name ?? ''} ${userDetails?.attributes?.last_name}`, sales_person_email: userDetails?.email ?? '' } },
                    { value: selectedCurrency?.symbol ?? '', label: 'Currency code' },
                    { value: values?.selectedOption, label: 'Payment Option' },
                    { value: payload.cf_exchange_rate, label: 'Exchange Rate' },
                    { value: payload.delivery_address.email, label: 'Customer Email' },
                    { value: userDetails?.email, label: 'Agent Email' },
                    { value: 'Obana Logistics', label: 'Carrier Name' },
                    { value: 'Pending', label: 'Shipment Status' },
                    { label: 'OFFERED_PRICE_PER_UNIT', value: checkoutPayload?.cartItems?.items.map((item) => ({ item_id: item.item_id, unit_price: item.rate, quantity: item.quantity })) },
                ],
                line_items: checkoutPayload?.cartItems?.items.map((item) => ({ name: item.name, rate: item.rate, quantity: item.quantity, item_id: item.item_id, item_total: item.total_price })),
            };
            const endpoint = `/requests/zoho/orders?${new URLSearchParams(params)}`;
            const endpoint1 = `/requests/zoho-book/create-quote`;
            const response = await apiService.post({ url: endpoint1, payload: payload1 });
            if (response.data.code === 0) {
                notifyQuoteSubmitted();
                await apiService.put({ url: `/cart/remove-product`, payload: { clearAll: true } });
                return;
            }
            const orderData = response?.data;
            const salesorder = orderData?.salesorder;
            const order_id = salesorder?.salesorder_id;
            if (!order_id) throw new Error('Failed to create order - no order ID returned');
            const shipmentSummary = orderData?.shipment_summary;
            const shipmentResults = orderData?.shipment_results;
            if (shipmentSummary) {
                if (shipmentSummary.status === 'all_shipments_created') {
                    toast({ title: 'Order & shipments created', description: orderData.message || `All ${shipmentSummary.successful} shipment(s) created.`, status: 'success', duration: 5000, isClosable: true });
                } else if (shipmentSummary.status === 'partial_shipments_created') {
                    const failedVendors = shipmentResults?.failed || [];
                    toast({ title: 'Order created — partial shipment success', description: `${orderData.message}\n\nFailed: ${failedVendors.map((f) => `${f.vendor_id}: ${f.error}`).join('; ')}`, status: 'warning', duration: 8000, isClosable: true });
                } else if (shipmentSummary.status === 'no_shipments_created') {
                    const failedVendors = shipmentResults?.failed || [];
                    toast({ title: 'Order created — shipment creation failed', description: `Order #${salesorder.salesorder_number} created but shipments failed.\n\n${failedVendors.map((f) => `\n• ${f.vendor_id}: ${f.error}`).join('')}\n\nPlease contact support.`, status: 'error', duration: 10000, isClosable: true });
                }
            } else {
                toast({ title: 'Order created successfully', description: orderData.message || 'Your order was created successfully.', status: 'success', duration: 5000, isClosable: true });
            }
            if (values?.selectedOption !== 'COD' && order_id) {
                makePayment(order_id, values?.selectedOption);
            } else {
                onPaymentSuccess();
                navigate('/dashboard/products');
                dispatch(view_cart());
            }
        } catch (error) {
            dispatch(view_cart());
            toast({ title: 'Error', description: error?.response?.data?.message || error?.message || 'Something went wrong', status: 'error', duration: 5000, isClosable: true });
            if (error?.code === 404) navigate('/dashboard/cart');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaladAfricaSuccess = () => {
        onSaladSuccessClose();
        navigate('/dashboard');
        dispatch(view_cart());
    };

    const isCOD = checkoutPayload?.dispatcher?.carrier_rate_description?.toLowerCase() === 'cash on delivery';
    const showsCODOnly = checkoutPayload?.dispatcher?.carrier_id === 'pickup' || isCOD;
    const addr = checkoutPayload?.address;
    const customerName = addr ? `${addr.first_name || ''} ${addr.last_name || ''}`.trim() : '';
    const addressLine = addr ? [addr.line1, addr.state, addr.country].filter(Boolean).join(', ') : '';
    const dispatcher = checkoutPayload?.dispatcher;
    const dispatcherName = dispatcher?.name ?? dispatcher?.carrier_name ?? '—';
    const dispatcherRate = dispatcher?.carrier_rate_description?.toLowerCase() ?? 'Pickup from store';
    const totalToPay = (Number(convertedAmount?.price ?? 0)) + (Number(newQuote?.price ?? 0));

    return (
        <Box py="20px" fontFamily={FONT}>
            <Box mb="18px">
                <Heading as="h2" fontSize={{ base: '18px', md: '20px' }} fontWeight="800" color={INK} fontFamily={FONT} letterSpacing="-0.02em" mb="4px">Review &amp; payment</Heading>
                <Text fontSize="12px" color={MUTED}>Confirm shipping details and choose how to pay</Text>
            </Box>
            <VStack align="stretch" spacing="10px" mb="20px">
                <SummaryCard label="SHIPPING &amp; BILLING ADDRESS" title={customerName || 'No customer info'} lines={[addressLine, addr?.phone]} onChange={() => setTabIndex(0)} />
                <SummaryCard label="SHIPPING METHOD" title={dispatcherName} lines={[`Rate: ${dispatcherRate}`]} onChange={() => setTabIndex(1)} />
            </VStack>
            <Formik initialValues={{ selectedOption: '' }} onSubmit={handleSubmit}>
                {({ values, isSubmitting, setFieldValue }) => (
                    <Form>
                        <Box mb="14px">
                            <Text fontSize="10px" color={FAINT} fontWeight="700" letterSpacing="0.08em" mb="2px">PAYMENT METHOD</Text>
                            <Text fontSize="11.5px" color={MUTED}>Choose how you'd like to pay</Text>
                        </Box>
                        <Field name="selectedOption">
                            {({ form }) => (
                                <VStack align="stretch" spacing="10px">
                                    <PaymentMethodCard id="COD" name="Cash on Delivery (COD)" description="Pay the courier when the order arrives" isSelected={values.selectedOption === 'COD'} onSelect={() => form.setFieldValue('selectedOption', 'COD')} />
                                    {!showsCODOnly && (
                                        <>
                                            <PaymentMethodCard id="PAYSTACK" name="Outright payment (Paystack)" description={`Paying in ${convertedAmount?.code || 'NGN'}`} logo={paystack} logoHeight="22px" isSelected={values.selectedOption === 'PAYSTACK'} isDisabled={!isNGN} disabledReason={!isNGN ? 'Only available for NGN orders' : null} onSelect={() => form.setFieldValue('selectedOption', 'PAYSTACK')} />
                                            <PaymentMethodCard id="STARTBUTTON" name="Outright payment (StartButton)" description={`Paying in ${convertedAmount?.code || 'NGN'}`} logo={startButton} logoHeight="30px" isSelected={values.selectedOption === 'STARTBUTTON'} onSelect={() => form.setFieldValue('selectedOption', 'STARTBUTTON')} />
                                            <PaymentMethodCard
                                                id="ONPSS"
                                                name="Order Now, Pay Small Small"
                                                description="Spread the customer's payments across flexible instalments"
                                                isSelected={values.selectedOption === 'ONPSS'}
                                                isDisabled={isCheckingONPSS}
                                                isDashed={!onpssUnlocked}
                                                helperText={!onpssUnlocked && !isCheckingONPSS ? 'Customer must register first — click to see steps' : null}
                                                rightBadge={isCheckingONPSS ? <Badge colorScheme="gray" fontSize="9px">Checking…</Badge> : onpssUnlocked ? <Badge colorScheme="green" fontSize="9px"><HStack spacing={1}><CheckCircleIcon boxSize="9px" /><span>Registered</span></HStack></Badge> : <Badge colorScheme="blue" variant="outline" fontSize="9px"><HStack spacing={1}><LockIcon boxSize="9px" /><span>Not registered</span></HStack></Badge>}
                                                onSelect={() => { if (!onpssUnlocked) setShowONPSSGate(true); else form.setFieldValue('selectedOption', 'ONPSS'); }}
                                            />
                                        </>
                                    )}
                                </VStack>
                            )}
                        </Field>
                        <Flex align="center" gap="6px" mt="14px" mb="18px" color={FAINT}>
                            <SecureLockIcon />
                            <Text fontSize="10.5px" color={FAINT} lineHeight="1.4">Your payment is encrypted and secure</Text>
                        </Flex>
                        <Divider borderColor={BORDER} mb="18px" />
                        {convertedAmount?.price != null && (
                            <Flex justify="space-between" align="center" mb="16px">
                                <Box>
                                    <Text fontSize="12px" color={MUTED} fontWeight="600">Total to pay</Text>
                                    <Text fontSize="10.5px" color={FAINT}>Includes shipping</Text>
                                </Box>
                                <Text fontSize="20px" color={INK} fontWeight="800" fontFamily={FONT}>
                                    {convertedAmount?.symbol || '₦'} {Number(totalToPay).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Text>
                            </Flex>
                        )}
                        <Button type="submit" w="full" h="48px" bg={NAVY} color="white" _hover={{ bg: NAVY_DEEP }} _active={{ bg: NAVY_DEEP }} _disabled={{ bg: BORDER, color: FAINT, cursor: 'not-allowed', _hover: { bg: BORDER } }} borderRadius="10px" fontWeight="700" fontSize="13px" fontFamily={FONT} isLoading={isSubmitting} loadingText="Processing" isDisabled={!values.selectedOption || isSubmitting}>Submit order</Button>
                        <Text fontSize="10.5px" color={FAINT} textAlign="center" mt="10px" lineHeight="1.4">By proceeding, you agree to be redirected to the payment provider.</Text>
                        {isSubmitting && (
                            <Modal isOpen={isSubmitting} isCentered closeOnOverlayClick={false} onClose={() => {}}>
                                <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
                                <ModalContent borderRadius="14px" fontFamily={FONT} mx="14px">
                                    <ModalBody p="32px" textAlign="center">
                                        <VStack spacing="14px">
                                            <Spinner size="xl" color={NAVY} thickness="3px" />
                                            <Heading fontSize="16px" fontWeight="800" color={INK} fontFamily={FONT}>Processing your order</Heading>
                                            <Text fontSize="12px" color={MUTED}>Please wait — don't close this tab</Text>
                                        </VStack>
                                    </ModalBody>
                                </ModalContent>
                            </Modal>
                        )}
                        {isSaladSuccessOpen && (
                            <Modal isOpen={isSaladSuccessOpen} onClose={onSaladSuccessClose} isCentered closeOnOverlayClick={false}>
                                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
                                <ModalContent borderRadius="14px" fontFamily={FONT} mx="14px">
                                    <ModalBody p="32px" textAlign="center">
                                        <VStack spacing="14px">
                                            <Flex w="64px" h="64px" borderRadius="full" bg="green.50" align="center" justify="center" color="green.500">
                                                <CheckCircleIcon boxSize="32px" />
                                            </Flex>
                                            <Heading fontSize="18px" fontWeight="800" color="green.600" fontFamily={FONT}>Order placed!</Heading>
                                            <Text fontSize="14px" fontWeight="700" color={INK}>Cash on Delivery confirmed</Text>
                                            <Text fontSize="12px" color={MUTED}>The order has been created. Payment will be collected on delivery.</Text>
                                            <Box bg={SURFACE_ALT} p="14px" borderRadius="10px" w="full" border="1px solid" borderColor={BORDER}>
                                                <Text fontSize="11px" color={FAINT} fontWeight="700" letterSpacing="0.06em" mb="4px">ORDER TOTAL</Text>
                                                <Text fontSize="16px" color={INK} fontWeight="800">
                                                    <WalletHistoryPrice price={(checkoutPayload?.cartItems?.cart_total_price ?? 0) + (checkoutPayload?.dispatcher?.amount ?? 0)} />
                                                </Text>
                                            </Box>
                                        </VStack>
                                    </ModalBody>
                                    <ModalFooter justifyContent="center" borderTop="1px solid" borderColor={BORDER} p="14px 20px">
                                        <Button bg={NAVY} color="white" _hover={{ bg: NAVY_DEEP }} fontWeight="700" fontSize="13px" fontFamily={FONT} borderRadius="10px" h="44px" px="24px" onClick={handleSaladAfricaSuccess}>Continue to dashboard</Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>
                        )}
                    </Form>
                )}
            </Formik>
            {showONPSSGate && <ONPSSGateModal isOpen={showONPSSGate} onClose={() => setShowONPSSGate(false)} hasApplication={hasExistingApplication} />}
        </Box>
    );
};

export default PaymentOptions;